import { EventEmitter, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { LocalStoreService } from '@frontend/vanilla/core';

import { PinnedBets } from '../../my-bets-base/models/my-bets-response';
import { MyBetsSummaryService } from '../../my-bets-base/my-bets-summary.service';

@Injectable()
export class MyBetsPinningService {
    private static storageKey = 'PinnedBets';

    onPinned = new EventEmitter<string>();
    onUnpinned = new EventEmitter<string>();
    private betNumbers: PinnedBets[];

    constructor(
        private localStoreService: LocalStoreService,
        myBetsSummary: MyBetsSummaryService,
        private myBetsConfig: MyBetsConfig,
        private toaster: ToasterService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
    ) {
        this.betNumbers = this.restore();
        myBetsSummary.summaryUpdate
            .pipe(takeUntilDestroyed())
            .subscribe((summary) => this.cleanupPins(summary.betNumbers, summary.isMigrationSummary));
    }

    togglePin(bet: PinnedBets): void {
        const itemIndex = this.betNumbers.findIndex((b) => b.betslipNumber === bet.betslipNumber);
        if (itemIndex > -1) {
            this.betNumbers.splice(itemIndex, 1);
            this.onUnpinned.emit(bet.betslipNumber);
        } else {
            if (this.betNumbers.length < this.myBetsConfig.maxPins) {
                this.betNumbers.unshift(bet);
                this.onPinned.emit(bet.betslipNumber);
            } else {
                const message = this.sitecoreMyBets.myBets.MaxPinInfo?.replace('{max}', this.myBetsConfig.maxPins.toString()) || '';
                this.toaster.open(message, { duration: 2000 });
            }
        }
        this.save();
    }

    isPinned(betNumber: string, isMigratedBet: boolean): boolean {
        return this.betNumbers.findIndex((b) => b.betslipNumber === betNumber && b.isMigratedBet === isMigratedBet) > -1;
    }

    getPinnedBetIds(isMigratedList: boolean | false): string[] {
        return this.myBetsConfig.enablePinning ? this.betNumbers.filter((b) => b.isMigratedBet === isMigratedList).map((b) => b.betslipNumber) : [];
    }

    private cleanupPins(openBetNumbers: string[], isMigrationSummary: boolean): void {
        if (this.betNumbers.length) {
            this.betNumbers.forEach((bn) => {
                if (openBetNumbers.indexOf(bn.betslipNumber) === -1 && bn.isMigratedBet === isMigrationSummary) {
                    const idx = this.betNumbers.findIndex((b) => b.betslipNumber === bn.betslipNumber);
                    this.betNumbers.splice(idx, 1);
                }
            });

            this.save();
        }
    }

    private restore(): PinnedBets[] {
        try {
            const storedValue = this.localStoreService.get(MyBetsPinningService.storageKey);
            if (Array.isArray(storedValue)) {
                return storedValue ? (storedValue as PinnedBets[]) : [];
            }
        } catch {}

        return [];
    }

    private save(): void {
        this.localStoreService.set(MyBetsPinningService.storageKey, this.betNumbers);
    }
}
